import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export type AuraLevel = 'Initiate' | 'Curator' | 'Tastemaker' | 'Connoisseur' | 'Icon';

export interface AuraPointsAction {
  type: 'generate_look' | 'share_community' | 'receive_like' | 'complete_archive' | 'daily_login' | 'save_outfit' | 'add_item';
  points: number;
  label: string;
}

export const AURA_ACTIONS: Record<string, AuraPointsAction> = {
  generate_look: { type: 'generate_look', points: 10, label: 'Generated a look' },
  share_community: { type: 'share_community', points: 25, label: 'Shared to syndicate' },
  receive_like: { type: 'receive_like', points: 5, label: 'Received appreciation' },
  complete_archive: { type: 'complete_archive', points: 50, label: 'Archive milestone' },
  daily_login: { type: 'daily_login', points: 10, label: 'Daily presence' },
  save_outfit: { type: 'save_outfit', points: 15, label: 'Saved an edit' },
  add_item: { type: 'add_item', points: 5, label: 'Expanded archive' },
};

export const AURA_LEVELS: { level: AuraLevel; minPoints: number; maxPoints: number }[] = [
  { level: 'Initiate', minPoints: 0, maxPoints: 99 },
  { level: 'Curator', minPoints: 100, maxPoints: 499 },
  { level: 'Tastemaker', minPoints: 500, maxPoints: 999 },
  { level: 'Connoisseur', minPoints: 1000, maxPoints: 4999 },
  { level: 'Icon', minPoints: 5000, maxPoints: Infinity },
];

export const getAuraLevel = (points: number): AuraLevel => {
  for (const { level, minPoints, maxPoints } of AURA_LEVELS) {
    if (points >= minPoints && points <= maxPoints) {
      return level;
    }
  }
  return 'Initiate';
};

export const getLevelProgress = (points: number): { current: number; next: number; percentage: number; nextLevel: string } => {
  const currentLevelData = AURA_LEVELS.find(l => points >= l.minPoints && points <= l.maxPoints);
  if (!currentLevelData || currentLevelData.level === 'Icon') {
    return { current: points, next: points, percentage: 100, nextLevel: 'Icon' };
  }
  
  const currentIndex = AURA_LEVELS.indexOf(currentLevelData);
  const nextLevelData = AURA_LEVELS[currentIndex + 1];
  
  const pointsInLevel = points - currentLevelData.minPoints;
  const levelRange = nextLevelData.minPoints - currentLevelData.minPoints;
  const percentage = Math.min((pointsInLevel / levelRange) * 100, 100);
  
  return {
    current: points,
    next: nextLevelData.minPoints,
    percentage,
    nextLevel: nextLevelData.level,
  };
};

export const useAuraPoints = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);

  const currentPoints = profile?.aura_points ?? 0;
  const currentLevel = (profile?.aura_level as AuraLevel) ?? 'Initiate';

  const addPoints = useCallback(async (actionType: keyof typeof AURA_ACTIONS, showToast = true) => {
    if (!user || isUpdating) return;

    const action = AURA_ACTIONS[actionType];
    if (!action) return;

    setIsUpdating(true);

    try {
      const newPoints = currentPoints + action.points;
      const newLevel = getAuraLevel(newPoints);
      const leveledUp = newLevel !== currentLevel;

      const { error } = await supabase
        .from('profiles')
        .update({ aura_points: newPoints })
        .eq('id', user.id);

      if (error) throw error;

      await refreshProfile();

      if (showToast) {
        toast({
          title: leveledUp ? `Level Up! ${newLevel}` : `+${action.points} Aura`,
          description: leveledUp 
            ? `You've ascended to ${newLevel}. Your style resonates.`
            : action.label,
        });
      }
    } catch (error) {
      console.error('Error updating aura points:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [user, currentPoints, currentLevel, isUpdating, refreshProfile]);

  return {
    points: currentPoints,
    level: currentLevel,
    levelProgress: getLevelProgress(currentPoints),
    addPoints,
    isUpdating,
    AURA_LEVELS,
    AURA_ACTIONS,
  };
};
